/**
 * Which face each name on the marketing site wears.
 *
 * Separate from the components that draw them because more than one surface
 * needs it: the hero room, and the product shots beside it. One map means the
 * same agent is the same person wherever a visitor meets it, which is the
 * whole reason a slot is durable in the product too.
 *
 * **Every slot here is named, never derived.** The pool draws real people, and
 * a public page shows the ones somebody chose, so it may not hash a name into
 * a slot and hope. `SAFE_SLOTS` below is what anything new draws from.
 */
import type { FaceStyle } from '@stagepost/web/components/primitives'

/**
 * The pool the marketing site draws from.
 *
 * The FaceContext default is `patterns`, the render-time floor for a page with
 * no organisation behind it, and it is what leaves a bare `<Face name=…/>` a
 * grey disc. The floor stays where it is; this site opts in, to the same pool
 * a new organisation actually starts on.
 */
export const MARKETING_FACE_STYLE: FaceStyle = 'historical'

/**
 * The cast, and who each of them is.
 *
 * Chosen by looking at the pool at 30px, which is the size the hero draws and
 * where a silhouette and a block of colour are the entire difference between
 * two faces: white halo, red headdress, dreadlocks, pink, a trumpet, a bowler
 * hat. Martin keeps the monochrome one, because the blue ring already says he
 * is a person and the colour is better spent on the agents.
 */
export const MARKETING_FACES: Record<string, string> = {
  Martin: 'face:36',          // Charlie Chaplin
  'phase-plan': 'face:02',    // Albert Einstein
  'cursor-tests': 'face:05',  // Frida Kahlo
  'remote-store': 'face:17',  // Bob Marley
  'db-schema': 'face:34',     // Elton John
  'traefik-certs': 'face:35', // Louis Armstrong
}

/**
 * Slots anything new on this site may use, in the order they were judged.
 *
 * Two filters, both applied by looking rather than by reasoning. The figure has
 * to be someone nobody has to think twice about seeing on a homepage, which
 * rules out the wartime leaders, the conquerors and the revolutionaries the
 * pool also contains. And it has to survive being 30px across, which rules out
 * anyone whose whole distinguishing feature is a face rather than a hat, a
 * hairline or a colour.
 *
 * The six in `MARKETING_FACES` come off the front of this list. Take the next
 * unused one rather than inventing a slot.
 */
export const SAFE_SLOTS: readonly string[] = [
  'face:02', // Albert Einstein, white halo
  'face:05', // Frida Kahlo, red headdress
  'face:17', // Bob Marley, dreadlocks
  'face:34', // Elton John, pink and star glasses
  'face:35', // Louis Armstrong, dark suit and a trumpet
  'face:36', // Charlie Chaplin, bowler hat, monochrome
  'face:20', // Bruce Lee, yellow
  'face:26', // Jimi Hendrix, red headband
  'face:14', // Marilyn Monroe, blonde halo
  'face:18', // Vincent van Gogh, red beard and a sunflower
  'face:27', // Audrey Hepburn, black updo and pearls
  'face:30', // John Lennon, round glasses
  'face:12', // Elvis Presley, black quiff
  'face:08', // Marie Curie, bun and a green flask
  'face:16', // Pablo Picasso, bald and striped
  'face:10', // William Shakespeare, ruff collar
]
